import React, { useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import { profileIcons, ProfileIcon, getIconsByCategory } from '../../utils/profileIcons';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface ProfileIconSelectorProps {
  open: boolean;
  onClose: () => void;
  currentIcon?: string;
  onSelect: (iconId: string) => void;
  loading?: boolean;
}

const ProfileIconSelector: React.FC<ProfileIconSelectorProps> = ({
  open,
  onClose,
  currentIcon,
  onSelect,
  loading = false
}) => {
  const [selectedCategory, setSelectedCategory] = useState<'diverse' | 'animals' | 'fantasy' | 'professions' | 'expressions'>('diverse');
  const [hoveredIcon, setHoveredIcon] = useState<string | null>(null);

  const categories = [
    { value: 'diverse' as const, label: 'Diverse' },
    { value: 'animals' as const, label: 'Animals' },
    { value: 'fantasy' as const, label: 'Fantasy' },
    { value: 'professions' as const, label: 'Professions' },
    { value: 'expressions' as const, label: 'Expressions' }
  ];

  const filteredIcons = getIconsByCategory(selectedCategory);

  const handleSelect = (iconId: string) => {
    onSelect(iconId);
  };

  return (
      <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 3 },
          maxHeight: { xs: '100vh', sm: '90vh' },
          m: { xs: 0, sm: 2 },
          height: { xs: '100vh', sm: 'auto' }
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" fontWeight={600}>
            Choose Your Profile Icon
          </Typography>
          <IconButton onClick={onClose} size="small">
            ×
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Tabs
            value={selectedCategory}
            onChange={(e, newValue) => setSelectedCategory(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              mb: 2
            }}
          >
            {categories.map((category) => (
              <Tab
                key={category.value}
                label={category.label}
                value={category.value}
                sx={{ textTransform: 'none', fontWeight: 500 }}
              />
            ))}
          </Tabs>
        </Box>

        <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ maxHeight: { xs: 'calc(100vh - 200px)', sm: '60vh' }, overflowY: 'auto', p: { xs: 0.5, sm: 1 } }}>
          {filteredIcons.map((icon) => {
            const isSelected = currentIcon === icon.id;
            const isHovered = hoveredIcon === icon.id;

            return (
              <Grid item xs={4} sm={3} md={2} key={icon.id}>
                <Paper
                  elevation={isSelected ? 4 : isHovered ? 2 : 1}
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    textAlign: 'center',
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'all 0.2s ease',
                    border: isSelected ? '2px solid' : '2px solid transparent',
                    borderColor: isSelected ? 'primary.main' : 'transparent',
                    bgcolor: isSelected ? 'primary.light' : 'background.paper',
                    minHeight: { xs: 90, sm: 100 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    touchAction: 'manipulation',
                    WebkitTapHighlightColor: 'transparent',
                    '&:active': {
                      transform: 'scale(0.95)',
                      bgcolor: 'action.selected'
                    },
                    '@media (hover: hover)': {
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4,
                        bgcolor: 'action.hover'
                      }
                    }
                  }}
                  onClick={() => handleSelect(icon.id)}
                  onMouseEnter={() => setHoveredIcon(icon.id)}
                  onMouseLeave={() => setHoveredIcon(null)}
                >
                  {isSelected && (
                    <CheckCircleIcon
                      sx={{
                        position: 'absolute',
                        top: { xs: 2, sm: 4 },
                        right: { xs: 2, sm: 4 },
                        color: 'primary.main',
                        fontSize: { xs: 18, sm: 20 }
                      }}
                    />
                  )}
                  <Typography
                    variant="h4"
                    sx={{
                      fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                      mb: { xs: 0.25, sm: 0.5 },
                      lineHeight: 1
                    }}
                  >
                    {icon.emoji}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: { xs: '0.65rem', sm: '0.7rem' },
                      color: 'text.secondary',
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      width: '100%',
                      textAlign: 'center'
                    }}
                  >
                    {icon.name}
                  </Typography>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        {currentIcon && (() => {
          const selectedIcon = getIconsByCategory(selectedCategory).find(i => i.id === currentIcon);
          const displayName = selectedIcon?.name || currentIcon;
          return (
            <Chip
              label={`Selected: ${displayName}`}
              color="primary"
              size="small"
            />
          );
        })()}
      </DialogActions>
    </Dialog>
  );
};

export default ProfileIconSelector;
